import { useState, type FormEvent } from 'react';
import { BrandLockup } from '../components/Brand';
import { ErrorMessage, Field } from '../components/ui';
import { IconMoon, IconSun } from '../components/icons';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useTheme } from '../lib/theme';

/**
 * Sperrbildschirm für den erzwungenen Passwortwechsel (Review M1).
 *
 * `must_change_password` wurde bis zur Härtung gesetzt, zurückgesetzt und in
 * der Anmeldeantwort mitgeliefert – aber nirgends durchgesetzt. Ein
 * Startpasswort, das ein Administrator per Telefon oder Chat weitergibt, blieb
 * damit unbegrenzt gültig: Wer die Seite neu lud oder die API direkt ansprach,
 * arbeitete einfach weiter.
 *
 * Die Middleware lässt in diesem Zustand nur noch `/me`, `/me/password` und
 * `/logout` durch. Deshalb ein eigener Bildschirm statt eines Dialogs über der
 * Anwendung: Jede andere Abfrage im Hintergrund liefe in einen Fehler.
 */
export function PasswordChangePage() {
  const { refreshUser, logout, user } = useAuth();
  const { resolved, toggle } = useTheme();

  const [aktuell, setAktuell] = useState('');
  const [neu, setNeu] = useState('');
  const [wiederholung, setWiederholung] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (neu !== wiederholung) {
      setError(new Error('Die beiden Eingaben stimmen nicht überein.'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/auth/me/password', { currentPassword: aktuell, newPassword: neu });
      // Die Änderung beendet serverseitig alle Sitzungen – auch die eigene.
      // Deshalb neu anmelden lassen, statt den Zustand hier zu flicken.
      await logout();
      await refreshUser();
    } catch (caught) {
      setError(caught);
      setBusy(false);
    }
  };

  return (
    <div className="login-page">
      <main className="login-main" style={{ gridColumn: '1 / -1' }}>
        <div className="login-box">
          <div className="stack" style={{ marginBottom: 20, gap: 18 }}>
            <BrandLockup height={54} />
            <div className="row between">
              <div>
                <strong style={{ fontSize: 16 }}>Neues Passwort vergeben</strong>
                <div className="small subtle">
                  Für {user?.email} ist ein Passwortwechsel hinterlegt. Bis dahin bleibt die Anwendung gesperrt.
                </div>
              </div>
              <button className="btn ghost icon-only" onClick={toggle} title="Design umschalten">
                {resolved === 'dark' ? <IconSun /> : <IconMoon />}
              </button>
            </div>
          </div>

          <form className="stack" onSubmit={submit}>
            <ErrorMessage error={error} />

            <Field label="Aktuelles Passwort" required>
              <input
                type="password"
                value={aktuell}
                autoComplete="current-password"
                autoFocus
                onChange={(event) => setAktuell(event.target.value)}
                required
              />
            </Field>

            <Field label="Neues Passwort" hint="Mindestens 12 Zeichen." required>
              <input
                type="password"
                value={neu}
                autoComplete="new-password"
                onChange={(event) => setNeu(event.target.value)}
                required
              />
            </Field>

            <Field label="Wiederholung" required>
              <input
                type="password"
                value={wiederholung}
                autoComplete="new-password"
                onChange={(event) => setWiederholung(event.target.value)}
                required
              />
            </Field>

            <button className="btn primary" type="submit" disabled={busy} style={{ width: '100%', padding: 10 }}>
              {busy ? 'Wird gespeichert …' : 'Passwort speichern'}
            </button>

            <button className="btn ghost" type="button" onClick={() => void logout()} style={{ width: '100%' }}>
              Abmelden
            </button>
          </form>
        </div>
      </main>
    </div>
  );
}
